import type { ReactNode } from 'react'

/**
 * Page scaffolding.
 *
 * Every screen was hand-rolling the same flex column and heading block, and
 * they had drifted apart. These put the layout in one place so a change to
 * spacing or vertical rhythm happens once.
 */

interface ScreenProps {
  children: ReactNode
  /** Centre everything vertically. For terminal states and spinners. */
  centered?: boolean
}

/**
 * There is deliberately no `footer` slot and no growing spacer.
 *
 * Pushing a control to the bottom of the viewport put it below the fold on a
 * short screen - on iOS it only appeared on the rubber-band - and left a lake
 * of whitespace on a tall one. Everything sits in flow, after the words that
 * ask for it.
 */
export function Screen({ children, centered }: ScreenProps) {
  if (centered) {
    return (
      <div className="wrap wrap-centered center">
        <div>{children}</div>
      </div>
    )
  }

  return (
    <div className="wrap">
      <div>{children}</div>
    </div>
  )
}

interface HeadProps {
  /** Small uppercase line above the title. */
  eyebrow?: ReactNode
  title: ReactNode
  /** One line under the title. */
  lede?: ReactNode
  /** Rendered between eyebrow and title - the activity icon and time. */
  above?: ReactNode
}

export function Head({ eyebrow, title, lede, above }: HeadProps) {
  return (
    <>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      {above}
      <h1>{title}</h1>
      {lede && <p className="muted">{lede}</p>}
    </>
  )
}
